/* ZIVOL P8.2: the free-gift sync. Loaded as a module on EVERY page (snippets/cart-drawer.liquid), deferred by nature.

   THE RULE. A lamp-set variant lists its gift variants in the metafield zivol.gifts. The page prints that as a map, { "<set variant id>": ["<gift variant id>", ...] } in a
   <script type="application/json" data-zvs-gift-map>: the bag prints the map for the products in the cart (snippets/cart-products.liquid: drawer and cart page, re-rendered after
   every cart change), the product page prints its own product's (sections/zvs-buy.liquid). Every such script in the DOM is merged. For each pair (gift G, set S): required = the
   summed quantity of the set lines of variant S; actual = the summed quantity of the gift lines of variant G marked `_gift_for` = S. Too few: one /cart/add.js with the missing
   gifts (gifts listed in reverse so the first gift lists first). Too many, or a gift line nothing requires (an orphan, or one added bare): one /cart/update.js (0 removes). A line is a gift line when
   its product_type is "Free gift" (the three gift products) or its variant id is a value in the map.
   MIGRATION. Old lines (P8 carried a visible "Free gift" property, P8.1 a hidden `_gifts`) are rewritten IN PLACE with /cart/change.js (the line key, its quantity, its
   properties without those two) before anything else, so the buyer's lamp is never added twice and "You save" survives. The gift lines are then added as usual.
   NOTHING IS GUESSED. If the page holds no map, or the cart has a set line of a product the map does not cover (a stale page restored from the back/forward cache, a bag drawn before the add),
   the bag section is re-fetched once and read again; if it still does not cover the cart, this pass does nothing. A gift is never removed on the strength of a map that cannot know the cart.

   REFRESH. The same path the theme uses (assets/zv-nc-offer.js is the in-house precedent): a CartLinesUpdateEvent whose promise carries the new cart is dispatched on document; the
   drawer's component re-fetches its own section when the promise carries no section HTML, assets/cart-icon.js recounts the sets from detail.items. The event's action is always
   'update', so the drawer never opens by itself.

   TRIGGERS (every one ends in syncGifts()): page load; pageshow from the back/forward cache; the cart drawer opening; every cart change the theme makes (its CartLinesUpdateEvent: add from
   the product form, stepper, remove, offer button: after the event's promise settles).
   ORDER. Shopify lists the newest line first, so assets/zvs.js adds the gifts BEFORE the lamp (capture-phase submit on #zl-form), and holds this sync off (window.zvsGiftHold, an 8 s
   failsafe that expires by itself) until the lamp's own event settles; then this sync finds the gifts already there and adds nothing, or removes them as orphans if the lamp add failed.
   LOOP SAFETY: (1) the events this file dispatches carry `zvsSync` and are ignored by its own listener; (2) ONE pass runs at a time: a trigger that arrives while it runs sets a flag and
   schedules exactly one more pass after it, never a parallel one; (3) a pass makes one correction and never re-triggers itself; (4) at most 3 passes per run. Every failure is swallowed:
   the bag must never break on this. Nothing here touches the checkout. */
import { fetchConfig } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const FOR = '_gift_for';
const OLD_PROPS = ['Free gift', '_gifts']; // P8 visible property, P8.1 hidden property: stripped from the lamp line
const GIFT_TYPE = 'Free gift'; // product_type of the three gift products (cart JSON carries no tags)
const MAX_PASSES = 3;

let running = null;
let rerun = false;

const cartUrl = () => (window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart';
const readCart = async () => {
  const res = await fetch(`${cartUrl()}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' });
  return res.json();
};
const post = async (url, body) => {
  const res = await fetch(url, fetchConfig('json', { body: JSON.stringify(body) }));
  const json = await res.json();
  if (json.status || json.errors) throw new Error(json.description || json.message || 'cart');
  return json;
};

/** Every gift map in `root` merged: { found, map: {setId: [giftId]}, covered: Set(productId) }. found is false when there is none or one cannot be read. */
const readMaps = (root) => {
  const out = { found: false, map: {}, covered: new Set() };
  const scripts = root.querySelectorAll('script[data-zvs-gift-map]');
  if (!scripts.length) return out;
  out.found = true;
  for (const script of scripts) {
    try {
      Object.assign(out.map, JSON.parse(script.textContent || '{}'));
    } catch (e) {
      out.found = false; // a map that cannot be read is a map we do not have
    }
    for (const id of (script.dataset.products || '').split(',')) if (id) out.covered.add(id);
  }
  return out;
};

const giftIdsOf = (maps) => new Set(Object.values(maps.map).flat().map(String));
const isGift = (item, giftIds) => item.product_type === GIFT_TYPE || giftIds.has(String(item.variant_id));
/** A set line whose product the maps do not cover: the maps cannot say what it needs. */
const isUncovered = (item, maps, giftIds) =>
  !isGift(item, giftIds) && !maps.covered.has(String(item.product_id)) && !(String(item.variant_id) in maps.map);

/** The maps that describe this cart, or null when none can be trusted. Re-fetches the bag section once when the page's maps do not cover the cart. */
const resolveMaps = async (cart) => {
  let maps = readMaps(document);
  if (!maps.found) return null;
  if (!cart.items.some((it) => isUncovered(it, maps, giftIdsOf(maps)))) return maps;

  const holder = document.querySelector('script[data-zvs-gift-map]')?.closest('cart-items-component');
  const sectionId = holder && holder.dataset.sectionId;
  if (!sectionId) return null;
  const html = await sectionRenderer.renderSection(sectionId, { cache: false, mode: holder.closest('#cart-drawer') ? 'hydration' : 'full' });
  const fresh = readMaps(new DOMParser().parseFromString(html, 'text/html'));
  if (!fresh.found) return null;
  maps = { found: true, map: { ...maps.map, ...fresh.map }, covered: new Set([...maps.covered, ...fresh.covered]) };
  return cart.items.some((it) => isUncovered(it, maps, giftIdsOf(maps))) ? null : maps;
};

/* present with ANY value, empty included: Shopify keeps a blanked property as "Free gift": "", so a line the blanking version already migrated is still old */
const isOldLine = (item, giftIds) => !isGift(item, giftIds) && OLD_PROPS.some((key) => item.properties && key in item.properties);
/* /cart/change.js with `properties` replaces the line's whole property set, so the old keys are simply left out */
const withoutOldProps = (props) => Object.fromEntries(Object.entries(props).filter(([key]) => !OLD_PROPS.includes(key)));

/** What must change, from one cart.js snapshot: { updates: {key: qty}, adds: [{id, quantity, properties}] } or null when it is already right. */
const plan = (cart, maps) => {
  const giftIds = giftIdsOf(maps);
  const required = new Map(); // "gift|set" -> { gift, set, quantity }
  for (const it of cart.items) {
    if (isGift(it, giftIds)) continue; // a gift line never requires gifts
    for (const gift of maps.map[String(it.variant_id)] || []) {
      const key = `${gift}|${it.variant_id}`;
      const entry = required.get(key) || { gift: String(gift), set: String(it.variant_id), quantity: 0 };
      entry.quantity += it.quantity;
      required.set(key, entry);
    }
  }
  const have = new Map(); // "gift|set" -> gift lines carrying that marker ("" = unmarked: required by no one)
  for (const it of cart.items) {
    if (!isGift(it, giftIds)) continue;
    const key = `${it.variant_id}|${(it.properties && it.properties[FOR]) || ''}`;
    have.set(key, [...(have.get(key) || []), it]);
  }
  const updates = {};
  const adds = [];
  for (const key of new Set([...required.keys(), ...have.keys()])) {
    const want = required.has(key) ? required.get(key).quantity : 0;
    const lines = have.get(key) || [];
    const present = lines.reduce((n, it) => n + it.quantity, 0);
    if (present > want) {
      let keep = want;
      for (const it of lines) {
        const qty = Math.min(it.quantity, keep);
        if (qty !== it.quantity) updates[it.key] = qty;
        keep -= qty;
      }
    } else if (present < want) {
      const { gift, set } = required.get(key);
      adds.push({ id: Number(gift), quantity: want - present, properties: { [FOR]: set } });
    }
  }
  adds.reverse(); // newest lists first: the first gift is added last
  return Object.keys(updates).length || adds.length ? { updates, adds } : null;
};

/** One correction (migration, then the quantity fix), then the theme's own drawer refresh. */
const correct = async (cart, maps, oldLines, first) => {
  const deferred = CartLinesUpdateEvent.createPromise();
  const ev = new CartLinesUpdateEvent({
    action: 'update',
    context: 'cart',
    lines: [
      ...oldLines.map((it) => ({ id: it.key, quantity: it.quantity })),
      ...(first ? Object.keys(first.updates).map((key) => ({ id: key, quantity: first.updates[key] })) : []),
      ...(first ? first.adds.map((a) => ({ merchandiseId: String(a.id), quantity: a.quantity })) : []),
    ],
    promise: deferred.promise,
  });
  deferred.promise.catch(() => {}); // a failed correction is swallowed below; the base promise is never left unhandled
  ev.zvsSync = true; // our own listener skips it
  document.dispatchEvent(ev);
  try {
    for (const it of oldLines) {
      await post(`${cartUrl()}/change.js`, { id: it.key, quantity: it.quantity, properties: withoutOldProps(it.properties) });
    }
    let after = cart;
    let p = first;
    if (oldLines.length) {
      after = await readCart(); // keys and merged lines changed: plan again on what is really there
      p = plan(after, maps);
    }
    if (p && Object.keys(p.updates).length) await post(`${cartUrl()}/update.js`, { updates: p.updates });
    if (p && p.adds.length) await post((window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add', { items: p.adds });
    if (oldLines.length || p) after = await readCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(after),
      detail: { items: after.items, itemCount: after.item_count, source: 'zvs-gift-sync', didError: false },
    });
  } catch (err) {
    deferred.reject(err);
    throw err;
  }
};

const pass = async () => {
  if (window.zvsGiftHold > Date.now()) return; // a deadline set by zvs.js, so a stale value expires by itself; zvs.js is between "gifts added" and "lamp added": the gifts are not orphans yet
  const cart = await readCart();
  const maps = await resolveMaps(cart);
  if (!maps) return;
  const giftIds = giftIdsOf(maps);
  const oldLines = cart.items.filter((it) => isOldLine(it, giftIds));
  const first = oldLines.length ? null : plan(cart, maps);
  if (oldLines.length || first) await correct(cart, maps, oldLines, first);
};

export function syncGifts() {
  if (running) {
    rerun = true;
    return running;
  }
  running = (async () => {
    try {
      let n = 0;
      do {
        rerun = false;
        await pass();
        n += 1;
      } while (rerun && n < MAX_PASSES);
    } catch (e) {
      /* swallowed: the bag must never break on the sync; the next trigger reads the cart again */
    } finally {
      running = null;
    }
  })();
  return running;
}

// 1. page load, and the back/forward cache
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => syncGifts(), { once: true });
else syncGifts();
window.addEventListener('pageshow', (e) => {
  if (e.persisted) syncGifts();
});

// 2. the cart drawer opening (it bubbles from <theme-drawer>)
document.addEventListener('theme-drawer:open', (e) => {
  if (e.target instanceof Element && e.target.id === 'cart-drawer') syncGifts();
});

// 3. every cart change the theme makes (after its request settled); our own events are skipped
document.addEventListener(StandardEvents.cartLinesUpdate, (e) => {
  if (e.zvsSync) return;
  Promise.resolve(e.promise)
    .catch(() => {})
    .then(() => {
      window.zvsGiftHold = 0; // the lamp add (or its failure) has settled: from here the cart is the truth
      return syncGifts();
    });
});
